'use client';
/* eslint-disable unicorn/no-null -- Client logger redacts context by returning `null` when headers cannot be captured. */
import { LogLevel } from '@logtail/next';
import { mapValues } from 'es-toolkit';
import { isPlainObject } from 'es-toolkit/predicate';
import type { JSX } from 'react';
import { useCallback, useMemo } from 'react';
import { env } from '@/env';
import { useLogger as useLogtail } from '@/lib/better-stack-logger.client';
import { isProductionRuntime } from '@/lib/runtime-flags';
import { isAutomationRuntime } from '@/lib/runtime-mode';

const snapshotHeaders = (headers: Headers) => {
  try {
    return Object.fromEntries(headers.entries());
  } catch {
    return;
  }
};

const snapshotResponse = (response: Response) => ({
  ok: response.ok,
  status: response.status,
  statusText: response.statusText,
  url: response.url,
  redirected: response.redirected,
  type: response.type,
  bodyUsed: response.bodyUsed,
  headers: snapshotHeaders(response.headers),
});

const sanitizeForLogging = (value: unknown, seen = new WeakSet<object>()): unknown => {
  if (!value || typeof value !== 'object') {
    return value;
  }

  if (value instanceof Response) {
    return snapshotResponse(value);
  }

  if (value instanceof Headers) {
    return snapshotHeaders(value);
  }

  if (seen.has(value)) {
    return '[Circular]';
  }
  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((entry) => sanitizeForLogging(entry, seen));
  }

  if (value instanceof Error) {
    const details: Record<string, unknown> = {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
    const ownProperties = Object.getOwnPropertyNames(value);
    for (const property of ownProperties) {
      if (property in details) {
        continue;
      }
      const propertyValue: unknown = Reflect.get(value, property);
      details[property] = sanitizeForLogging(propertyValue, seen);
    }
    return details;
  }

  if (!isPlainObject(value)) {
    return value;
  }

  return mapValues(value as Record<string, unknown>, (entry) => sanitizeForLogging(entry, seen));
};

interface PublicLogger {
  debug: (message: string, data?: unknown) => void;
  error: (message: string, data?: unknown) => void;
  flush: () => Promise<void>;
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
  with: (fields: Record<string, unknown>) => PublicLogger;
}

export function useLogger(source = 'client'): PublicLogger {
  const source_ = source;
  const shouldSendRemoteLogs = isProductionRuntime() || env.NEXT_PUBLIC_VERCEL_ENV === 'production';
  const shouldLogToConsole = env.NEXT_PUBLIC_ENABLE_RT_DEBUG || !isAutomationRuntime();
  const logtailOptions = useMemo(
    () => (shouldSendRemoteLogs ? {} : { logLevel: LogLevel.off }),
    [shouldSendRemoteLogs]
  );
  // Create Better Stack client once per component instance
  const bs = useLogtail(logtailOptions);

  const make = useCallback(
    (baseFields: Record<string, unknown> = {}): PublicLogger => {
      const merge = (extra?: Record<string, unknown>) => ({ ...baseFields, ...extra });
      const buildPayload = (data?: unknown) => {
        if (data === undefined) {
          return merge();
        }

        const sanitized = sanitizeForLogging(data);
        if (isPlainObject(sanitized)) {
          return merge(sanitized);
        }

        return merge({ value: sanitized });
      };
      return {
        info: (message: string, data?: unknown) => {
          const payload = buildPayload(data);
          bs.info(message, payload);
          if (shouldLogToConsole) {
            console.info(`[INFO][${source_}] ${message}`, payload);
          }
        },
        error: (message: string, data?: unknown) => {
          const payload = buildPayload(data);
          bs.error(message, payload);
          console.error(`[ERROR][${source_}] ${message}`, payload);
        },
        warn: (message: string, data?: unknown) => {
          const payload = buildPayload(data);
          bs.warn(message, payload);
          if (shouldLogToConsole) {
            console.warn(`[WARN][${source_}] ${message}`, payload);
          }
        },
        debug: (_message: string, _data?: unknown) => {
          // Intentionally silent to avoid console noise in clients; debug traffic stays in devtools if needed.
        },
        with: (fields: Record<string, unknown>) => make({ ...baseFields, ...fields }),
        flush: async () => {
          // Browser logger does not buffer entries, so there is nothing to flush.
        },
      } satisfies PublicLogger;
    },
    [bs, source_, shouldLogToConsole]
  );

  // Return a stable logger instance for this source
  return useMemo(() => make({ source: source_ }), [make, source_]);
}

export function useInteractionLogger() {
  const logger = useLogger('interaction');
  return {
    logClick: (element: string, metadata?: Record<string, unknown>) => {
      logger.info('User clicked', { element, ...metadata, timestamp: new Date().toISOString() });
    },
    logNavigation: (from: string, to: string, metadata?: Record<string, unknown>) => {
      logger.info('User navigated', { from, to, ...metadata, timestamp: new Date().toISOString() });
    },
    logFormSubmit: (formName: string, metadata?: Record<string, unknown>) => {
      logger.info('Form submitted', { formName, ...metadata, timestamp: new Date().toISOString() });
    },
    logError: (error: Error | string, context?: Record<string, unknown>) => {
      logger.error('Client error', {
        error: error instanceof Error ? { message: error.message, stack: error.stack } : error,
        ...context,
        timestamp: new Date().toISOString(),
      });
    },
    logPerformance: (metric: string, value: number, metadata?: Record<string, unknown>) => {
      logger.info('Performance metric', {
        metric,
        value,
        unit: 'ms',
        ...metadata,
        timestamp: new Date().toISOString(),
      });
    },
  };
}

export function useAPILogger() {
  const logger = useLogger('api');
  const slowThresholdMs = 2000;
  return {
    logRequest: (endpoint: string, method: string, metadata?: Record<string, unknown>) => {
      const metadataRecord = metadata as { shouldLog?: unknown } | undefined;
      const shouldLog = metadataRecord?.shouldLog === true;
      if (shouldLog) {
        logger.info('API request', { endpoint, method, ...metadata, timestamp: new Date().toISOString() });
      }
    },
    logResponse: (endpoint: string, status: number, duration: number, metadata?: Record<string, unknown>) => {
      const { logLevel, ...details } = metadata ?? {};
      const level = typeof logLevel === 'string' ? logLevel : undefined;
      const logData = { endpoint, status, durationMs: duration, ...details, timestamp: new Date().toISOString() };
      if (status >= 500 || level === 'error') {
        logger.error('API response', logData);
        return;
      }
      if (status >= 400) {
        logger.warn('API response (client)', logData);
        return;
      }
      if (duration >= slowThresholdMs || level === 'warn') {
        logger.warn('API response (slow)', logData);
        return;
      }
      if (level === 'info') {
        logger.info('API response', logData);
      }
    },
    logError: (endpoint: string, error: Error | string, metadata?: Record<string, unknown>) => {
      logger.error('API error', {
        endpoint,
        error: error instanceof Error ? { message: error.message, stack: error.stack } : error,
        ...metadata,
        timestamp: new Date().toISOString(),
      });
    },
  };
}

// No Web Vitals component needed
export function WebVitals(): JSX.Element | null {
  return null;
}
